import React, { useState, useEffect } from 'react';
import api from './api';
import fileDownload from 'js-file-download';
import './SalaryPanel.css';

const formatINR = (n) =>
  (Number(n ?? 0)).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });

const SalaryPanel = ({ employeeId }) => {
  const [salary, setSalary] = useState(null);
  const [error, setError] = useState('');
  const [month, setMonth] = useState("");
  //const [year, setYear] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  /*const authHeader = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('employeeToken')}`,
      Accept: 'application/json'
    },
    withCredentials: true
  };
*/

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };


  // Fetch current user role
  useEffect(() => {
    const fetchCurrentEmployee = async () => {
      try {
        const res = await api.get('/api/me');
        const userRole = res.data.role || "";
        setRole(userRole);

        if (userRole.toLowerCase() === 'hr') {
          await fetchEmployees();
        } else {
          const empId = employeeId || res.data.id;
          setSelectedEmployee(empId);
          setEmployees([{ id: empId, name: res.data.name || 'Yourself' }]);
        }
      } catch (err) {
        setError('Failed to fetch employee data');
      } finally {
        setLoading(false);
      }
    };
    fetchCurrentEmployee();
  }, []);

  // Fetch employees list for HR
  const fetchEmployees = async () => {
    try {
      const response = await api.get('/api/employees');
      const empList = response.data.data || response.data.employees || response.data || [];
      setEmployees(empList);
    } catch (error) {
      setError('Failed to fetch employee list');
    }
  };

  // Fetch salary whenever employee / month / year changes
  useEffect(() => {
    if (!selectedEmployee || !month || !year) return;

    setIsLoading(true);
    setError('');

    api.post('/api/salary/calculate', { employee_id: selectedEmployee, month, year })
      .then(res => {
        setSalary(res.data);
        setError('');
        setShowErrorPopup(false);
        setIsLoading(false);
      })
      .catch(err => {
        console.log(" API Error:", err.response?.data); // Debug

        if (err.response && err.response.data) {
          const res = err.response.data;


          if (res.error) {
            setError(res.error);


          } else if (res.message) {
            setError(res.message);


          } else if (res.errors) {
            const firstKey = Object.keys(res.errors)[0];
            setError(res.errors[firstKey][0]);
          } else {
            setError("Something went wrong. Please try again.");
          }

          setShowErrorPopup(true);
        } else {
          setError("Failed to fetch salary details. Please try again.");
          setShowErrorPopup(true);
        }

        setIsLoading(false);
      });
  }, [selectedEmployee, month, year]);

  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
    setSalary(null);
  };

  const downloadSlip = async () => {

    if (!selectedEmployee) {
      setError("Please select an employee.");
      setShowErrorPopup(true);
      return;
    }
    if (!month) {
      setError("Please select a month.");
      setShowErrorPopup(true);
      return;
    }
    if (!year) {
      setError("Please select a year.");
      setShowErrorPopup(true);
      return;
    }

    if (!salary?.id) {
      setError("Salary not available for selected filters.");
      setShowErrorPopup(true);
      return;
    }

    try {
      const response =api.get(`/api/salary/slip/${salary.id}`, { responseType: 'blob' });
      fileDownload(response.data, `salary_slip_${month}_${year}.pdf`);
    } catch {
      setError("Failed to download salary slip");
      setShowErrorPopup(true);
    }
  };


  const months = [
    { value: 1, name: "January" }, { value: 2, name: "February" },
    { value: 3, name: "March" }, { value: 4, name: "April" },
    { value: 5, name: "May" }, { value: 6, name: "June" },
    { value: 7, name: "July" }, { value: 8, name: "August" },
    { value: 9, name: "September" }, { value: 10, name: "October" },
    { value: 11, name: "November" }, { value: 12, name: "December" }
  ];

  const years = [2023, 2024, 2025];

  if (loading) {
    return (
      <div className="loader-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  //  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="salary-panel">
      <h2 className="panel-heading">Salary Panel</h2>
      <div className="salary-card">

        {/* HR dropdown */}
        {role.toLowerCase() === 'hr' && (
          <div className="employee-select">
            <label>Select Employee:</label>
            <select value={selectedEmployee} onChange={handleEmployeeChange}>
              <option value="">--Select Employee--</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Month filter */}
        <div className="filter-select">
          <label>Month:</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value="">-- Select Month --</option>
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.name}</option>
            ))}
          </select>

        </div>

        {/* Year filter */}
        <div className="filter-select">
          <label>Year:</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {salary && (
          <>
            <h3>Salary for {months.find(m => m.value === month)?.name} {year}</h3>
            <div className="salary-details-grid">
              <div className="earnings">
                <h4>Earnings</h4>
                <p><span>Base Salary:</span> {formatINR(salary.base_salary)}</p>
                <p><span>Performance Bonus:</span> {formatINR(salary.performance_bonus)}</p>
                <p><span>Other Allowances:</span> {formatINR(salary.other_allowances)}</p>
                {/* Extra Work / Overtime */}
                <p><span>Total Extra Work Hours:</span> {formatHours(salary.total_extra_work_hours)}</p>
                <p><span>Extra Work Pay:</span> {formatINR(salary.extra_work_pay)}</p>
                {/*<p><span>Total Overtime Hours:</span> {formatHours(salary.total_overtime_hours)}</p>
                <p><span>Overtime Pay:</span> {formatINR(salary.overtime_pay)}</p>*/}
              </div>
              <div className="deductions">
                <h4>Deductions</h4>
                <p><span>Leave Deduction:</span> {formatINR(salary.leave_deduction)}</p>
                <p><span>Half-Day Deduction:</span> {formatINR(salary.half_day_deduction)}</p>
                <p><span>Professional Tax:</span> {formatINR(salary.professional_tax)}</p>
              </div>
            </div>
            <div className="salary-summary">
              <p><strong>Gross Salary:</strong> {formatINR(salary.gross_salary)}</p>
              <p><strong>Total Deductions:</strong> {formatINR(salary.total_deductions)}</p>
              <p className="net-salary"><strong>Net Salary:</strong> {formatINR(salary.net_salary)}</p>
            </div>
            <button className="download-btn" onClick={downloadSlip}>Download Salary Slip</button>

          </>
        )}
      </div>

      {/* Error Popup Modal */}
      {showErrorPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h4 style={{ color: "red" }}>Error</h4>
            <p>{error}</p>
            <button onClick={() => setShowErrorPopup(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryPanel;
